import { ChoiceLocation } from './types'
import { ChoiceMultiprofile } from '../../components/ChoiceMultiprofile'
import {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { IS_PRODUCTION } from '@/constants'
import { ClinicAndFlags } from '@/modules/Auth/auth-types'
import { User } from '@/modules/Auth/classes/User'
import { getCache, removeCache, setCache } from '@/utils/cache'
import { setRefreshToken, setToken } from '@/utils/token'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from '@conexasaude/conexa-hero'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import service from '../../auth-services'
import themeServices from '@/theme/theme.services'
import { RenderIf } from '@/components/RenderIf'
import LoadingPage from '@/components/LoadingPage'
import { TermsAndConditionsModal } from '../../components/TermsAndConditionsModal'
import { DependentsModal } from '@/modules/Activation/components/DependentsModal'
import { showErrorToast } from '@/modules/Activation/utils/requisitionErrors'
import { MinorAccessRequestModal } from '../../components/MinorAccessRequestModal'
import { useStyles } from './styles'
import { AnalyticEvent } from '@/modules/Activation/types/en-us'
import { SEND_TO } from '@/modules/Signup/constants'
import { getDigitsFromString } from '@/utils/getDigitsFromString'
import { sha256Hash } from '@conexasaude/analytics'
import { usePatientStore } from '@/modules/Activation/store/PatientStore/patient.store'
import { useNavigation } from '@/hooks/useNavigation'
import { formatCpfNumber } from '@/utils/masks/cpf'
import GetDependentData from '@/modules/Activation/mappers/GetDependentData'
import { AccessRequestSentModal } from '../../components/AccessRequestSentModal'
import { useCheckMinorsTermAcceptance } from '../../queries'
import { useMinorAccessRequestStore } from '../../store/MinorAccessRequest.store'
import { crmEvent } from '@/utils/sendCRMEvent'
import { buildServicesForCRMEvent } from '@/utils/buildServicesForCRMEvent'

const Choice: FunctionComponent = () => {
  const history = useHistory()
  const location = useLocation<ChoiceLocation>()
  const styles = useStyles()

  const { logAnalyticsEvent } = useFirebaseContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()
  const { users, choiceUser, setUserClinic, signOut } = useAuthContext()
  const { setThemeColors } = useThemeGeneratorContext()
  const { showModal } = useModalContext()
  const { patient, setPatientData, setHolder, resetHolder, resetPatientData } =
    usePatientStore()

  const [activeTab, setActiveTab] = useState<string | null>('activeUsers')
  const [alertIsOpen, setAlertIsOpen] = useState(true)
  const [currentUser, setCurrentUser] = useState(null)
  const [currentProfileId, setCurrentProfileId] = useState(0)
  const [loading, setLoading] = useState(false)
  const [termsModalActive, setTermsModalActive] = useState(false)
  const { setMinorAccessRequestData } = useMinorAccessRequestStore()
  const { mutateAsync: checkMinorsTermAcceptance } =
    useCheckMinorsTermAcceptance()
  const navigate = useNavigation()

  const settingUpAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      const normalizedCPF = getDigitsFromString(currentUser?.cpf)
      const hashedCPF = sha256Hash(normalizedCPF)
      const eventName = eventDefinitions?.eventName || 'at_patient_multiprofile'
      logAnalyticsEvent(eventName, {
        interaction: `${eventDefinitions?.interaction}`,
        ...(eventDefinitions?.user_id && {
          user_id: eventDefinitions?.user_id,
        }),
        ...(eventDefinitions?.cnx_user_id && {
          cnx_user_id: eventDefinitions?.cnx_user_id,
        }),
        ...(hashedCPF && { cnx_user_id: hashedCPF }),
        ...(hashedCPF && { user_id: hashedCPF }),
        options: {
          sendTo: SEND_TO,
        },
      })
    },
    [currentUser?.cpf, logAnalyticsEvent],
  )

  const getClinicColors = useCallback(
    async (idClinic: number) => {
      try {
        const clinicColors = await themeServices.getClinicColorsById(idClinic)

        if (clinicColors) {
          setThemeColors(clinicColors)
        }
      } catch (error) {
        console.error('Erro ao buscar as cores da clínica:', error)
      }
    },
    [setThemeColors],
  )

  const redirectAfterChoice = useCallback(
    (pathname?: string) => {
      const linkToRedirectAfterLogin = getCache<string>(
        'link-to-redirect-after-login',
      )

      if (linkToRedirectAfterLogin || location?.state?.from) {
        let path = linkToRedirectAfterLogin ?? location?.state?.from?.pathname

        if (location?.state?.from?.search) {
          path = path + `/${location?.state?.from?.search}`
        }

        navigate(path)
        removeCache('link-to-redirect-after-login')

        return
      }

      history.push(pathname)
    },
    [history, location?.state?.from, navigate],
  )

  const shouldStayInMultiprofile = () => {
    const redirectToMultiprofile = users?.find(
      user => user?.holder === 'TITULAR' && user?.frontCnx,
    )
    return !!redirectToMultiprofile
  }
  const keepInMultiprofile = shouldStayInMultiprofile()

  const returnToLoginPage = useCallback(() => {
    logAnalyticsEvent('Pac_AtivacaoLogada_Voltar')
    signOut(true)
    if (location?.pathname === '/escolha-unificada') {
      if (IS_PRODUCTION) {
        window.location.href =
          'https://consultorioonline.psicologiaviva.com.br/login'
      } else {
        window.location.href =
          'https://hml-consultorioonline.psicologiaviva.com.br/login'
      }
    } else {
      history.push('/login')
    }
  }, [history, location?.pathname, logAnalyticsEvent, signOut])

  const handleShowMinorAccessRequestModal = useCallback(
    (user: User) => {
      setMinorAccessRequestData({
        idClinic: user?.clinic?.id,
        idPatient: user?.patientId,
        patientName: user?.patientName,
        cpf: user?.cpf,
        holder: user?.holder,
        clinicProduct: user?.clinic?.product,
        token: user.token,
        refreshToken: user?.refreshToken,
      })
      settingUpAnalyticsEvent({
        interaction: 'seen',
        eventName: 'at_patient_responsible_form_modal',
        patient_type: user?.holder,
        clinic_id: user?.clinic?.id,
        account_type: user?.clinic?.product,
      })
      updateSetLoading(false)
      setCurrentProfileId(0)
      signOut(false)
      showModal('GUARDIAN_ACCESS_REQUEST_MODAL', {
        persistent: true,
      })
    },
    [setMinorAccessRequestData, settingUpAnalyticsEvent, showModal, signOut],
  )

  const setIfHaveOneUser = useCallback(async () => {
    try {
      setCurrentUser(users[0])
      const token = users[0]?.token
      const refreshToken = users[0]?.refreshToken
      setToken(token)
      setRefreshToken(refreshToken)
      setCache('user', users[0]?.id)

      const termsChecked = await service.checkTermsAcceptance()

      const userClinic: ClinicAndFlags = await service.getClinic(
        users[0]?.token,
      )
      const clinicColors = await themeServices.getClinicColorsById(
        userClinic?.clinic?.idClinica,
      )
      const ip = await service.getUserIP().catch(() => {
        console.error('Erro ao pegar ip')
        return ''
      })
      if (!users[0]?.flagAccessedBefore)
        await service.validateFirstAccess(users[0]?.token)
      choiceUser(users[0]?.id, ip)

      if (users[0]?.isMinorPatient) {
        const acceptedTerm = checkMinorsTermAcceptance(users[0]?.token)
        if (!acceptedTerm) {
          handleShowMinorAccessRequestModal(users[0])
          return
        }
      }

      if (!termsChecked) {
        setTermsModalActive(true)
        showModal('TERMS_AND_CONDITIONS', {
          title: 'Termos de uso',
          persistent: true,
        })
        return
      }
      setThemeColors(clinicColors)
      setUserClinic(userClinic)
      setSpecialities(userClinic?.clinic)

      if (userClinic?.flags?.HabilitarAutenticacaoDoisFatoresLoginPaciente) {
        let twoFactorAuthRoute = '/autenticacao-dois-fatores'

        const uniqueCode = getCache<string>('unique-code')

        if (uniqueCode) {
          twoFactorAuthRoute += `/${uniqueCode}`
        }

        history.push(twoFactorAuthRoute, location?.state?.from)
        return
      }
      crmEvent('pac_acessou', {
        nome_clinica: users[0]?.clinic?.name,
        id_clinica: userClinic?.clinic?.idClinica,
        id_paciente: users[0]?.patientId,
        servicos_oferecidos: buildServicesForCRMEvent(userClinic),
      })
      redirectAfterChoice('/')
    } catch (e) {
      toast({
        message: 'Algo de errado ocorreu. Tente novamente',
        variant: 'error',
      })
    }
  }, [
    users,
    choiceUser,
    setThemeColors,
    setUserClinic,
    setSpecialities,
    redirectAfterChoice,
    checkMinorsTermAcceptance,
    handleShowMinorAccessRequestModal,
    showModal,
    history,
    location?.state?.from,
  ])

  const setUser = useCallback(
    async (user: User) => {
      setTermsModalActive(false)
      try {
        updateSetLoading(true)
        setCurrentProfileId(user.id)
        const userClinic: ClinicAndFlags = await service.getClinic(user?.token)
        const clinicColors = await themeServices.getClinicColorsById(
          userClinic?.clinic?.idClinica,
        )
        const ip = await service.getUserIP().catch(() => {
          console.error('Erro ao pegar ip')
          return ''
        })
        if (!user?.flagAccessedBefore)
          await service.validateFirstAccess(user?.token)
        choiceUser(user.id, ip)
        if (clinicColors) {
          setThemeColors(clinicColors)
        }
        setUserClinic(userClinic)
        setSpecialities(userClinic.clinic)

        if (userClinic?.flags?.PermiteGeolocalizacao) {
          navigator.geolocation.getCurrentPosition(position => {
            const coords = position.coords
            if (coords) {
              service.sendGeolocation({
                accuracy: coords.accuracy,
                altitude: coords.altitude,
                altitudeAccuracy: coords.altitudeAccuracy,
                heading: coords.heading,
                latitude: coords.latitude,
                longitude: coords.longitude,
                speed: coords.speed,
              })
            }
          })
        }

        if (userClinic?.flags?.HabilitarAutenticacaoDoisFatoresLoginPaciente) {
          let twoFactorAuthRoute = '/autenticacao-dois-fatores'

          const uniqueCode = getCache<string>('unique-code')

          if (uniqueCode) {
            twoFactorAuthRoute += `/${uniqueCode}`
          }

          history.push(twoFactorAuthRoute, location?.state?.from)
          return
        }

        redirectAfterChoice('/')
        logAnalyticsEvent('at_patient_multiprofile', {
          patientId: user?.patientId,
          email: user?.email,
          phone: user?.cellphone,
          interaction: 'authentication_success',
        })
        crmEvent('pac_acessou', {
          nome_clinica: user?.clinic?.name,
          id_clinica: userClinic?.clinic?.idClinica,
          id_paciente: user?.patientId,
          servicos_oferecidos: buildServicesForCRMEvent(userClinic),
        })
      } catch (e) {
        console.log(e)
        setLoading(false)
        setCurrentProfileId(0)
        toast({
          message: 'Algo de errado ocorreu. Tente novamente',
          variant: 'error',
        })
      }
    },
    [
      choiceUser,
      setUserClinic,
      setSpecialities,
      redirectAfterChoice,
      logAnalyticsEvent,
      setThemeColors,
      history,
      location?.state?.from,
    ],
  )

  const handleUserSelect = useCallback(
    async (user: User) => {
      try {
        if (user?.origin !== 'PV') {
          await service.validateToken(user?.token)

          updateSetLoading(true)
          setCurrentProfileId(user?.id)
          setCurrentUser(user)
          setToken(user?.token)
          setRefreshToken(user?.refreshToken)
          setCache('user', user?.id)

          const termsChecked = await service.checkTermsAcceptance()

          if (user?.isMinorPatient) {
            const acceptedTerm = await checkMinorsTermAcceptance(user?.token)
            if (!acceptedTerm) {
              handleShowMinorAccessRequestModal(user)
              return
            }
          }

          if (!termsChecked) {
            setTermsModalActive(true)
            showModal('TERMS_AND_CONDITIONS', {
              title: 'Termos de uso',
              persistent: true,
            })

            updateSetLoading(false)
            setCurrentProfileId(0)
            return
          }
          setUser(user)
        } else {
          window.location.href = user?.redirectUrl
        }
      } catch (err) {
        updateSetLoading(false)
        setCurrentProfileId(0)
        if (
          err?.response?.data?.toString() ===
            'Erro de validação na requisição no campo: token' ||
          err?.response?.data?.msg?.includes('INVALID_ACCESS_TOKEN')
        ) {
          signOut(true)
          history.push('/login')
        } else {
          showErrorToast(err)
        }
      }
    },
    [
      checkMinorsTermAcceptance,
      handleShowMinorAccessRequestModal,
      history,
      setUser,
      showModal,
      signOut,
    ],
  )

  useEffect(() => {
    resetPatientData()
    resetHolder()
    settingUpAnalyticsEvent({ interaction: 'seen' })

    const redirectSingleUserToHome = users?.length === 1 && !keepInMultiprofile

    if (!users) {
      history.push('/login', {
        from: location?.state?.from,
      })
    }
    if (!users?.length) {
      setTimeout(() => {
        history.push('/login', {
          from: location?.state?.from,
        })
        toast({
          message:
            'Não foi possível continuar. Entre em contato com a sua operadora de saúde ou empresa.',
          variant: 'error',
        })
      }, 2000)
    }
    if (redirectSingleUserToHome) {
      setIfHaveOneUser()
    }
    if (location?.pathname === '/escolha-unificada') {
      setCache('unified-redirect-multi-profile', true)
    } else {
      removeCache('unified-redirect-multi-profile')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const changeActiveTab = useCallback(() => {
    logAnalyticsEvent('Pac_Clinicas_VerContas')
    updateSetActiveTab('pendentUsers')
  }, [logAnalyticsEvent])

  const handleInactiveUserSelect = useCallback(
    (selectedUser: User) => {
      const holder = users?.find(
        user => user?.patientId === selectedUser?.responsable?.id,
      )
      setPatientData({
        id: selectedUser?.patientId,
        name: selectedUser?.name || selectedUser?.patientName || '',
        cpf: formatCpfNumber(selectedUser?.cpf) || '',
        cellphone: selectedUser?.cellphone || '',
        email: selectedUser?.email || '',
        isMinorPatient: selectedUser?.isMinorPatient,
        birthdate:
          selectedUser?.birthdate?.split('-')?.reverse()?.join('/') || '',
        ...(selectedUser?.holder !== 'TITULAR' && {
          dependentType: 'adultDependent',
        }),
        ...(selectedUser?.clinic && {
          clinic: {
            flagAllowBrazeSend: selectedUser?.flagAllowBrazeSend,
            ...selectedUser?.clinic,
            ...(selectedUser?.clinic?.product && {
              productTypeActivation: selectedUser?.clinic?.product,
            }),
            ...(selectedUser?.clinicIdentifier && {
              identifier: selectedUser?.clinicIdentifier,
            }),
          },
        }),
      })
      if (holder) {
        setHolder({
          id: holder?.id,
          name: holder?.name,
          cpf: holder?.cpf,
          token: holder?.token,
        })
      }
      if (selectedUser?.holder !== 'TITULAR' && selectedUser?.isMinorPatient) {
        navigate(
          `/ativacao/atualizar/dados-dependente/${selectedUser?.patientId}`,
        )
      } else {
        const firstUserWithToken = users?.find(user => !!user?.token)

        setPatientData({
          ...selectedUser,
          token: firstUserWithToken?.token,
        })
        navigate(`/ativacao/dados-usuario`)
      }
      getClinicColors(selectedUser?.clinic?.id)
    },
    [getClinicColors, navigate, setHolder, setPatientData, users],
  )

  const handleShowDependentsModal = useCallback(
    (user: User) => {
      settingUpAnalyticsEvent({
        eventName: 'at_patient_multiprofile_dependents_modal',
        interaction: 'seen',
      })
      setPatientData({
        ...patient,
        clinic: { ...user?.clinic, termURL: user?.termUrl },
      })
      setHolder({
        id: user?.id,
        name: user?.name,
        cpf: user?.cpf,
        token: user?.token,
      })
      setCurrentUser({
        ...user,
        adultDependents: GetDependentData.toDomain(user?.adultDependents),
        childrenDependents: GetDependentData.toDomain(user?.childrenDependents),
      })
      showModal('DEPENDENTS_MODAL', {
        title: 'Meus dependentes',
        width: 480,
      })
    },
    [patient, setHolder, setPatientData, settingUpAnalyticsEvent, showModal],
  )

  const activeUsers = users?.filter(user => user?.flagIsActive)
  const inactiveUsers = users?.filter(user => !user?.flagIsActive)

  const updateSetActiveTab = (activeTab: string) => {
    setActiveTab(activeTab)
  }

  const updateSetAlertIsOpen = (open: boolean) => {
    setAlertIsOpen(open)
  }

  const updateSetLoading = (loading: boolean) => {
    setLoading(loading)
  }

  const hasUsers = users?.length > 0
  const hasMultipleUsers = users?.length > 1
  const isSingleUser = users?.length === 1

  const enableMultiprofile =
    hasMultipleUsers || (hasUsers && keepInMultiprofile)
  const loadingOnNoTerms =
    isSingleUser && !termsModalActive && !keepInMultiprofile
  const uniqueById = dependents => {
    const seenIds = new Set()
    return dependents?.filter(dependent => {
      if (!seenIds.has(dependent.id)) {
        seenIds.add(dependent.id)
        return true
      }
      return false
    })
  }

  const uniqueAdultDependents = uniqueById(currentUser?.adultDependents)
  const uniqueChildDependents = uniqueById(currentUser?.childrenDependents)

  const setUserTermsAndConditionsModal = async () => {
    await setUser(currentUser)
    return true
  }

  return (
    <Fragment>
      <RenderIf condition={!users}>
        <LoadingPage withoutNav text="Estamos deixando tudo pronto pra você!" />
      </RenderIf>
      <RenderIf condition={loadingOnNoTerms}>
        <div css={styles.wrapper}>
          <LoadingPage withoutNav={true} />
        </div>
      </RenderIf>
      <RenderIf condition={enableMultiprofile}>
        <ChoiceMultiprofile
          activeTab={activeTab}
          activeUsers={activeUsers}
          alertIsOpen={alertIsOpen}
          currentProfileId={currentProfileId}
          currentUser={currentUser}
          inactiveUsers={inactiveUsers}
          loading={loading}
          termsModalActive={termsModalActive}
          users={users}
          actionBackStepper={returnToLoginPage}
          changeActiveTab={changeActiveTab}
          handleInactiveUserSelect={handleInactiveUserSelect}
          handleShowDependentsModal={handleShowDependentsModal}
          handleUserSelect={handleUserSelect}
          updateSetActiveTab={updateSetActiveTab}
          updateSetAlertIsOpen={updateSetAlertIsOpen}
          updateSetUser={setUser}
          updateSetLoading={updateSetLoading}
          enableMultiprofile={keepInMultiprofile}
        />
      </RenderIf>
      <TermsAndConditionsModal
        redirectTo={''}
        directLogin={setUserTermsAndConditionsModal}
        setLoading={updateSetLoading}
        userToken={currentUser?.token}
      />
      <DependentsModal
        adultDependents={uniqueAdultDependents}
        childDependents={uniqueChildDependents}
      />
      <MinorAccessRequestModal />
      <AccessRequestSentModal />
    </Fragment>
  )
}

export default Choice
